import { getSuggestionEmail } from '@utils/emailSuggestion';

type UserLike = { id: string; email?: string | null };
type EmailEntity = { users?: Array<{ email?: string | null }> | null };

const normalizeEmail = (email?: string | null) =>
  (email || '').trim().toLowerCase();

/**
 * Dedupe user rows by stable id and by normalized email, so duplicate contact
 * records (same person, different source/id) collapse to one visible row.
 */
export const dedupeUsersByIdAndEmail = <T extends UserLike>(
  users: T[]
): T[] => {
  const seenIds = new Set<string>();
  const seenEmails = new Set<string>();
  return users.filter((u) => {
    const email = normalizeEmail(u.email);
    if (seenIds.has(u.id)) return false;
    if (email && seenEmails.has(email)) return false;
    seenIds.add(u.id);
    if (email) seenEmails.add(email);
    return true;
  });
};

/**
 * "More on TaskTag" list: registered users from global search, excluding
 * anyone already visible as a contact row, already selected, or the current
 * user — mirrors ProjectInviteOrAddMember's _filteredTasktagUsers, plus
 * email-based contact exclusion since contact ids can differ across sources.
 */
export const filterTasktagUsers = <T extends UserLike>({
  tasktagUsers,
  contacts,
  selectedUserIds,
  meId,
}: {
  tasktagUsers: T[];
  contacts: UserLike[];
  selectedUserIds: Set<string>;
  meId?: string;
}): T[] => {
  const contactIds = new Set(contacts.map((c) => c.id));
  const contactEmails = new Set(
    contacts.map((c) => normalizeEmail(c.email)).filter(Boolean)
  );
  return tasktagUsers.filter((u) => {
    const email = normalizeEmail(u.email);
    return (
      !selectedUserIds.has(u.id) &&
      !contactIds.has(u.id) &&
      !(email && contactEmails.has(email)) &&
      u.id !== meId
    );
  });
};

/**
 * Suggest an "Invite: <email>" item only when the email is not already
 * present as a contact, registered user, group member, team member, or an
 * already-selected email — mirrors ProjectInviteOrAddMember's suggestionList.
 * Returns the suggestion email, or null when none should be shown.
 */
export const buildEmailSuggestion = ({
  searchVal,
  contacts,
  tasktagUsers,
  groups,
  teamUsers,
  selectedEmails,
}: {
  searchVal: string;
  contacts: Array<{ email?: string | null }>;
  tasktagUsers: Array<{ email?: string | null }>;
  groups: EmailEntity[];
  teamUsers: Array<{ email?: string | null }>;
  selectedEmails: string[];
}): string | null => {
  const semail = getSuggestionEmail(searchVal);
  if (!semail) return null;

  const target = semail.toLowerCase();
  const matches = (email?: string | null) => normalizeEmail(email) === target;

  if (contacts.some((c) => matches(c.email))) return null;
  if (tasktagUsers.some((u) => matches(u.email))) return null;
  if (groups.some((g) => (g.users ?? []).some((u) => matches(u.email))))
    return null;
  if (teamUsers.some((u) => matches(u.email))) return null;
  if (selectedEmails.some((e) => e.trim().toLowerCase() === target))
    return null;

  return semail;
};
